import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isCancel } from 'axios';
import { v4 as uuid4 } from 'uuid';
import { icons } from '@ublend-npm/aulaui-next';
import { useSlateStatic, useReadOnly, ReactEditor } from 'slate-react';
import { useAulaEditorContext } from '../../EditorContext';
import { FrameLoader } from './FrameLoader';
import { extractIframeMessageData } from '../../lib/extractIframeMessageData';
import { BlockToolbar, BlockToolbarItem } from '../BlockToolbar/BlockToolbar';
import { getCurrentNode } from '../../lib/queries';
import { LtiEntity } from '../../Entities';
import { CollapsedLtiBlock, Form, IFrame, Wrapper } from './LtiBlock.styled';
import { LtiMenuOption, LtiMessage } from './ltiType';
import { Header, Title } from '../BlockLink/BlockLink.styled';
import { removeVoidElement } from '../../lib/commands';
import {
  Failed,
  FailedDescription,
  FailedIcon,
  BlockButton,
} from '../File/File.styled';
import { BlockElement } from '../../types';
import { requestHandler } from '../../utils/api';

export type LtiBlockProps = {
  /** The slate element for the LTI block */
  element: BlockElement;

  /** The title text that will be used for cases such as error block or collapsed mode */
  title: string;

  /** The icon that will be used for cases such as collapsed mode */
  icon: JSX.Element;

  /**
   * LTI consumer endpoint route for the given LTI block.
   * Excludes endpoint origin, because origin depends on configuration.
   * Example: panopto-lti-consumer
   */
  ltiEndpoint: string;

  /**
   * The callback function that gets returns data after
   * interacting with the LTI block. For example, on a panopto block,
   * after video content is selected, onMessage runs with the id of the video
   * @param data LTI message data that can contains linkId and/or contentId among others.
   */
  onMessage?: <T extends LtiMessage>(data: T) => void;

  /**
   * LTI menu comes with its default hover menu options such as delete and
   * expand/collapse menuOptions optional prop allows appending additional
   * menu options. LtiMenuOption type defines the required / optional fields
   * to describe the behaviour of the hover menu action.
   */
  menuOptions?: LtiMenuOption[];

  /**
   * The message prefix set by the lti-consumer handler.
   * See: serverless/lti-consumer/src/handlers/
   */
  messagePrefix?: string;

  /**
   * Enable LTI Block iframe scrolling
   */
  scrolling?: boolean;
};

// Replace with iframe resize
const FRAME_HEIGHT = '400px';

export const LtiBlock = ({
  title,
  icon: ltiIcon,
  element,
  ltiEndpoint,
  onMessage,
  menuOptions = [],
  messagePrefix,
  scrolling = false,
}: LtiBlockProps) => {
  const { current: iframeId } = useRef(uuid4());
  const formRef = useRef<HTMLFormElement>(null);
  const formRefTab = useRef<HTMLFormElement>(null);

  const [loading, setLoading] = useState(false);
  const [ltiRequest, setLtiRequest] = useState<{
    url: string;
    method: string;
    body: {
      [key: string]: string;
    };
  } | null>(null);

  const editor = useSlateStatic();
  const isReadOnly = useReadOnly();
  const { entities, config, updateEntity } = useAulaEditorContext();
  const { current: ltiConsumerEndpoint } = useRef(
    `${config.apiUrl}/${ltiEndpoint}`,
  );

  const { entity: entityId } = element;
  const selectedEntity = entities[entityId] as LtiEntity;
  const isSelected = getCurrentNode(editor) === element;
  const [hovering, setHovering] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [requestedHeight, setRequestedHeight] = useState(0);
  const [tabLtiRequest, setTabLtiRequest] = useState<{
    url: string;
    method: string;
    body: {
      [key: string]: string;
    };
  } | null>(null);
  if (selectedEntity?.type !== 'lti') {
    throw new Error('not allowed to render LtiBlock without lti entity');
  }

  const { data: metaData, linkId, params, failed } = selectedEntity;

  const { userId, spaceId } = config;

  const tryHandleIframeResizeEvent = useCallback(
    ({ origin, data }: MessageEvent) => {
      if (
        ltiRequest?.url.startsWith(origin) &&
        typeof data === 'string' &&
        data.includes('lti.frameResize')
      ) {
        const parsedData = JSON.parse(data);
        setRequestedHeight((height) => {
          if (height > parsedData.height) {
            return height;
          }
          return parsedData.height;
        });
      }
    },
    [ltiRequest],
  );

  const handleOnMessage = useCallback(
    (event: MessageEvent) => {
      const { origin, data } = event;

      tryHandleIframeResizeEvent(event);

      if (
        onMessage &&
        !isReadOnly &&
        messagePrefix &&
        ltiConsumerEndpoint.startsWith(origin) &&
        typeof data === 'string' &&
        data.includes(messagePrefix)
      ) {
        const messageData = extractIframeMessageData({
          event,
          messagePrefix,
        });

        if (messageData.linkId === linkId) {
          onMessage(messageData as any);
        }
        ReactEditor.focus(editor);
      }
    },
    [
      onMessage,
      messagePrefix,
      ltiConsumerEndpoint,
      linkId,
      editor,
      isReadOnly,
      tryHandleIframeResizeEvent,
    ],
  );

  const fetchLtiRequest = async (abortSignal: AbortSignal) => {
    const { data } = await requestHandler({
      url: ltiConsumerEndpoint,
      method: 'post',
      userId,
      data: {
        ...(params || {}),
        spaceId,
        linkId,
      },
      signal: abortSignal,
    });

    return data;
  };

  useEffect(() => {
    const getLtiReq = async ({ signal: abortSignal }: AbortController) => {
      if (failed) {
        return;
      }
      try {
        setLoading(true);
        const data = await fetchLtiRequest(abortSignal);

        // if there is an error message in payload,
        //  then mark the entity as failed
        if (data?.errors?.length > 0) {
          console.error(data.errors[0]);
          updateEntity(entityId, { failed: true });
          return;
        }

        setLtiRequest(data);
      } catch (ex) {
        /**
         * Aborting the request (see below) throws an AbortError exception
         * We must not mark the lti entity as failed if this error is thrown
         * (which can happen when LtiBlock is unexpectedly unmounted / rerendered)
         */
        if (!isCancel(ex)) {
          console.error(ex);
          updateEntity(entityId, { failed: true });
        }
      }
    };

    const abortController = new AbortController();
    void getLtiReq(abortController);

    return () => abortController.abort();
  }, [
    params,
    linkId,
    entityId,
    ltiConsumerEndpoint,
    spaceId,
    userId,
    metaData,
    updateEntity,
    failed,
  ]);

  useEffect(() => {
    if (ltiRequest) {
      formRef.current?.submit();
    }
  }, [ltiRequest]);

  useEffect(() => {
    window.addEventListener('message', handleOnMessage);
    return () => {
      window.removeEventListener('message', handleOnMessage);
    };
  }, [handleOnMessage]);

  useEffect(() => {
    if (tabLtiRequest) {
      formRefTab.current?.submit();
    }
  }, [tabLtiRequest]);

  const handleIFrameLoad = useCallback(() => {
    // Make sure we wait until we're loading the result of the lti request, sometimes onload
    // can occur before, for example in storybook
    if (ltiRequest) {
      setLoading(false);
    }
  }, [ltiRequest]);

  /**
   * Obtains the authenticated URL to LTI 1.1 provider and opens the URL in a new tab
   */
  const onClick = () => {
    const getLtiReq = async ({ signal: abortSignal }: AbortController) => {
      if (failed) {
        return;
      }
      try {
        const data = await fetchLtiRequest(abortSignal);

        // if there is an error message in payload,
        if (data?.errors?.length > 0) {
          console.error(data.errors[0]);
          return;
        }

        setTabLtiRequest(data);
      } catch (ex) {
        /**
         * Aborting the request (see below) throws an CancelledError exception
         * We must not mark the lti entity as failed if this error is thrown
         * (which can happen when LtiBlock is unexpectedly unmounted / rerendered)
         */
        if (!isCancel(ex)) {
          console.error(ex);
        }
      }
    };

    const abortController = new AbortController();
    void getLtiReq(abortController);
  };

  const toolbarItems = useMemo(
    (): BlockToolbarItem[] => [
      {
        icon: icons.Delete,
        title: 'Delete',
        onAction: () => removeVoidElement(editor, element),
        isVisible: !isReadOnly,
      },
      {
        title: collapsed ? 'Expand' : 'Collapse',
        icon: collapsed ? icons.Fullscreen : icons.FullscreenExit,
        onAction: () => setCollapsed(!collapsed),
        isVisible: !failed,
      },
      {
        title: 'Open in new tab',
        label: 'Open',
        icon: icons.OpenInNew,
        onAction: () => onClick(),
      },

      ...menuOptions.map((menuOption) =>
        // Translate menu options using the LtiMenuOption to the internal
        // BlockToolbarItem interface:
        ({
          icon: menuOption.icon,
          title: menuOption.label,
          onAction: menuOption.onClick,
          isVisible:
            (!failed || menuOption.showOnFailure) &&
            (!isReadOnly || menuOption.showOnReadOnly),
        }),
      ),
    ],
    [collapsed, editor, menuOptions, element, failed, isReadOnly],
  );

  // validate context
  if (!config.spaceId) {
    const message = 'Cannot display content. Space ID is unknown';
    console.error(message);
    return <p>{message}</p>;
  }

  // The toolbar is visible if the block is selected or hovered, and there
  // are items to display in the toolbar:
  const toolbarVisible =
    (isSelected || hovering) && toolbarItems.some((item) => item.isVisible);

  const blockToolbar = (
    <BlockToolbar isVisible={toolbarVisible} items={toolbarItems} />
  );

  const collapsedBlock = (
    <CollapsedLtiBlock isSelected={isSelected}>
      <Header>
        {ltiIcon}
        <Title> {title}</Title>
      </Header>
      <BlockButton onClick={() => setCollapsed(false)}>
        Click to expand
      </BlockButton>
    </CollapsedLtiBlock>
  );

  if (failed) {
    const onRetry = () => updateEntity(element.entity, { failed: false });
    return (
      <Failed
        isSelected={isSelected}
        data-testid="failed-lti-block"
        contentEditable={false}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        aria-current={isSelected}
      >
        {blockToolbar}
        <FailedIcon>
          <icons.ErrorIcon />
        </FailedIcon>
        <FailedDescription>
          {title} content failed to load.
          {!isReadOnly && (
            <>
              <br />
              <BlockButton onClick={onRetry}>Retry</BlockButton>
            </>
          )}
        </FailedDescription>
      </Failed>
    );
  }

  return (
    <Wrapper
      isSelected={isSelected}
      contentEditable={false}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      aria-current={isSelected}
      data-testid="lti-block"
    >
      {blockToolbar}
      {collapsed
        ? collapsedBlock
        : loading && <FrameLoader height={FRAME_HEIGHT} />}
      {ltiRequest && (
        <Form
          method={ltiRequest.method}
          action={ltiRequest.url}
          ref={formRef}
          target={iframeId}
        >
          {Object.entries(ltiRequest.body).map(([key, value]) => (
            <input key={key} name={key} value={value} readOnly />
          ))}
        </Form>
      )}
      {tabLtiRequest && (
        <Form
          method={tabLtiRequest.method}
          action={tabLtiRequest.url}
          ref={formRefTab}
          target="_blank"
        >
          {Object.entries(tabLtiRequest.body).map(([key, value]) => (
            <input key={key} name={key} value={value} readOnly />
          ))}
        </Form>
      )}
      <IFrame
        collapsed={collapsed}
        title={`lti-iframe-${iframeId}`}
        isLoading={loading}
        id={iframeId}
        name={iframeId}
        width="100%"
        height={requestedHeight || FRAME_HEIGHT}
        frameBorder="0"
        scrolling={scrolling ? 'yes' : 'no'}
        allow="fullscreen *;microphone *;camera *"
        onLoad={handleIFrameLoad}
        data-testid="lti-iframe"
      />
    </Wrapper>
  );
};
